import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calculator, Loader2 } from "lucide-react";
import { API, SERVICE_OPTIONS } from "@/lib/site";
import { Reveal } from "@/components/Reveal";

const REGIONS = [
  "Gauteng", "Western Cape", "KwaZulu-Natal", "Eastern Cape", "Free State",
  "Mpumalanga", "Limpopo", "North West", "Northern Cape", "Multiple / Nationwide",
];
const FREQUENCIES = ["Daily", "Weekly", "Ad-hoc / seasonal", "Monthly"];

export const EstimateTool = () => {
  const [form, setForm] = useState({
    region: REGIONS[0],
    serviceType: SERVICE_OPTIONS[0],
    vehicles: "2",
    frequency: FREQUENCIES[0],
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setEstimate(null);
    try {
      const { data } = await axios.post(`${API}/estimate`, form);
      setEstimate(data.estimate);
    } catch (err) {
      toast.error("Could not generate an estimate right now.", {
        description: "Please use the partnership enquiry form below instead.",
      });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-white/15 bg-[#080C14] px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]";
  const labelCls =
    "mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400";

  return (
    <section className="py-20 md:py-28" data-testid="estimate-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="flex items-center gap-3 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#FF6B00]">
                <span className="inline-block h-px w-8 bg-[#FF6B00]" aria-hidden="true" />
                AI Capacity Planner
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-4 font-display text-3xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-4xl lg:text-5xl">
                Get an indicative setup in seconds.
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-5 max-w-md text-base leading-relaxed text-slate-300">
                Describe your operation and our AI capacity planner suggests the vehicle mix and
                contract type that fits — indicative only, no obligation. Final pricing follows on
                signed scope.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-white/10 bg-[#0D1424] p-6 md:p-8">
                <form onSubmit={submit} data-testid="estimate-form" className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="est-region" className={labelCls}>Region</label>
                      <select id="est-region" value={form.region} onChange={set("region")} data-testid="estimate-input-region" className={`${inputCls} appearance-none`}>
                        {REGIONS.map((r) => <option key={r} value={r} className="bg-[#0D1424]">{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="est-service" className={labelCls}>Service Needed</label>
                      <select id="est-service" value={form.serviceType} onChange={set("serviceType")} data-testid="estimate-input-service" className={`${inputCls} appearance-none`}>
                        {SERVICE_OPTIONS.map((o) => <option key={o} value={o} className="bg-[#0D1424]">{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="est-vehicles" className={labelCls}>Vehicles Needed (1–20)</label>
                      <input id="est-vehicles" type="number" min="1" max="20" required value={form.vehicles} onChange={set("vehicles")} data-testid="estimate-input-vehicles" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="est-frequency" className={labelCls}>Frequency</label>
                      <select id="est-frequency" value={form.frequency} onChange={set("frequency")} data-testid="estimate-input-frequency" className={`${inputCls} appearance-none`}>
                        {FREQUENCIES.map((f) => <option key={f} value={f} className="bg-[#0D1424]">{f}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="est-notes" className={labelCls}>Notes (Optional)</label>
                    <input id="est-notes" type="text" value={form.notes} onChange={set("notes")} data-testid="estimate-input-notes" placeholder="e.g. retail DC to stores, ~150 parcels/day" className={inputCls} />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="estimate-submit-btn"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#FF6B00] px-8 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#080C14] transition-all hover:bg-[#E05E00] active:scale-[0.98] disabled:opacity-60"
                  >
                    {loading ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Planning capacity...</>
                    ) : (
                      <><Calculator className="h-4 w-4" /> Get Indicative Estimate</>
                    )}
                  </button>
                </form>

                {estimate && (
                  <div
                    data-testid="estimate-result"
                    className="mt-6 whitespace-pre-wrap rounded-xl border border-[#FF6B00]/40 bg-[#FF6B00]/[0.06] p-5 text-sm leading-relaxed text-slate-200"
                  >
                    {estimate}
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
